import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Badge, Heading, Text } from "@lendcord/concord";
import { ProfileField } from "@/features/profile/components/ProfileField";
import { ApplicationDetailViewSkeleton } from "./ApplicationDetailViewSkeleton";
import { useGetApplicationQuery } from "./api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { formatTimestampDate } from "@/app/dateFormat";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const STATUS_BADGE: Record<string, "success" | "warning" | "info" | "error"> = {
  PENDING: "warning",
  IN_PROGRESS: "info",
  APPROVED: "success",
  REJECTED: "error",
  DELETED: "error",
  CANCELLED: "info",
};

function formatDateTime(iso: string) {
  const time = new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  return `${formatTimestampDate(iso)}, ${time}`;
}

interface ApplicationDetailViewProps {
  applicationId: string;
  onBack: () => void;
}

// Read-only — sectioned the same way the "New Application" form is, for a
// consistent read/write pair. Fields still mirror ApplicationListItem's shape
// (no fabricated PAN/Aadhaar/documents/address fields the form collects but
// GET /onboarding/applications/{id} doesn't return).
export function ApplicationDetailView({ applicationId, onBack }: ApplicationDetailViewProps) {
  const intl = useIntl();
  const { data: application, error } = useGetApplicationQuery(applicationId);
  const loadError = error
    ? isQueryApiError(error)
      ? error.message
      : intl.formatMessage({ id: "lender.onboarding.detailLoadError" })
    : undefined;

  const backLink = (
    <div className="mb-4">
      <button
        type="button"
        onClick={onBack}
        aria-label={intl.formatMessage({ id: "lender.onboarding.backToApplications" })}
        className="text-sm font-medium text-accent-600 hover:underline"
      >
        ‹ <FormattedMessage id="lender.onboarding.backToApplications" />
      </button>
    </div>
  );

  if (loadError) {
    return (
      <div>
        {backLink}
        <AlertBanner status="error">{loadError}</AlertBanner>
      </div>
    );
  }

  if (!application) {
    return (
      <div>
        {backLink}
        <ApplicationDetailViewSkeleton />
      </div>
    );
  }

  return (
    <div>
      {backLink}

      <div className="flex items-center gap-3">
        <Heading level={2}>
          <FormattedMessage id="lender.onboarding.viewPageTitle" />
        </Heading>
        <Badge status={STATUS_BADGE[application.status] ?? "info"}>
          <FormattedMessage id={`lender.onboarding.status${application.status}`} />
        </Badge>
      </div>
      <Text as="p" size="sm" variant="muted" className="mt-1">
        <FormattedMessage id="lender.onboarding.generatedReadableId" values={{ id: application.readableId }} />
      </Text>

      <div className="mt-6 rounded-md border border-border bg-surface-raised">
        <section className="px-6 py-3">
          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.onboarding.applicationTypeHeading" />
          </Heading>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <ProfileField labelId="lender.onboarding.columnFullName" value={application.fullName} />
            <ProfileField
              labelId="lender.onboarding.columnApplicationType"
              value={
                application.applicationType === "BORROWER"
                  ? intl.formatMessage({ id: "lender.onboarding.applicationTypeBorrower" })
                  : intl.formatMessage({ id: "lender.onboarding.applicationTypeLender" })
              }
            />
            <ProfileField labelId="lender.onboarding.columnReadableId" value={application.readableId} />
          </div>
        </section>

        {application.applicationType === "BORROWER" && (
          <section className="px-6 py-3">
            <Heading level={4} className="text-accent-600">
              <FormattedMessage id="lender.onboarding.loanDetailsHeading" />
            </Heading>
            <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
              <ProfileField
                labelId="lender.onboarding.columnLoanAmount"
                value={application.loanAmount != null ? currencyFormatter.format(application.loanAmount) : "–"}
              />
            </div>
          </section>
        )}

        <section className="px-6 py-3">
          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.onboarding.locationHeading" />
          </Heading>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="sm:col-span-3">
              <ProfileField labelId="lender.onboarding.columnLocation" value={application.locationAddress ?? "–"} />
            </div>
            <ProfileField
              labelId="lender.onboarding.latitudeLabel"
              value={application.locationLatitude != null ? String(application.locationLatitude) : "–"}
            />
            <ProfileField
              labelId="lender.onboarding.longitudeLabel"
              value={application.locationLongitude != null ? String(application.locationLongitude) : "–"}
            />
          </div>
        </section>

        <section className="px-6 py-3">
          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.onboarding.additionalDetailsHeading" />
          </Heading>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <ProfileField labelId="lender.onboarding.centerNameLabel" value={application.centerName ?? "–"} />
            <ProfileField labelId="lender.onboarding.areaLabel" value={application.area ?? "–"} />
            <div className="sm:col-span-3">
              <ProfileField labelId="lender.onboarding.columnRemarks" value={application.remarks ?? "–"} />
            </div>
          </div>
        </section>

        <div className="px-6 pb-6">
          <Text size="xs" variant="muted">
            <FormattedMessage
              id="lender.onboarding.createdAtMeta"
              values={{ date: formatDateTime(application.createdAt) }}
            />
          </Text>
        </div>
      </div>
    </div>
  );
}
